import {type FlowEntity, type FlowSetup, type ScenarioRow, type TerminalNode} from '../types'

const buildSetupPathsToTerminal = (
  setup: FlowSetup,
  entities: FlowEntity[],
  terminalNode: TerminalNode
): string[] => {
  const nameMap = new Map<string, string>(entities.map((e) => [e.id, e.label]))
  nameMap.set(terminalNode.id, terminalNode.label)

  const adjacency = new Map<string, string[]>()
  const hasIncoming = new Set<string>()
  for (const link of setup.links) {
    const targets = adjacency.get(link.sourceNodeId) ?? []
    targets.push(link.targetNodeId)
    adjacency.set(link.sourceNodeId, targets)
    hasIncoming.add(link.targetNodeId)
  }

  const paths: string[] = []

  const traverse = (nodeId: string, currentPath: string[]) => {
    if (nodeId === terminalNode.id) {
      paths.push(currentPath.map((id) => nameMap.get(id) ?? id).join(' \u2192 '))
      return
    }
    const neighbors = adjacency.get(nodeId) ?? []
    for (const next of neighbors) {
      if (!currentPath.includes(next)) {
        traverse(next, [...currentPath, next])
      }
    }
  }

  for (const entity of entities) {
    if (!hasIncoming.has(entity.id) && adjacency.has(entity.id)) {
      traverse(entity.id, [entity.id])
    }
  }

  return paths
}

export const buildFlowScenarios = (
  setups: FlowSetup[],
  entities: FlowEntity[],
  terminalNode: TerminalNode,
  setupLabels: string[]
): ScenarioRow[] => {
  const pathToSetups = new Map<string, string[]>()

  setups.forEach((setup, index) => {
    const setupLabel = setupLabels[index]
    const paths = buildSetupPathsToTerminal(setup, entities, terminalNode)
    for (const path of paths) {
      const labels = pathToSetups.get(path) ?? []
      labels.push(setupLabel)
      pathToSetups.set(path, labels)
    }
  })

  const scenarioPaths = Array.from(pathToSetups.entries())

  return scenarioPaths.map(([path, labels]) => ({
    path,
    setups: labels.join(', ')
  }))
}
